import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "wouter";
import { ErpShell } from "../components/layout/ErpShell.js";
import { CommissionCascade } from "../components/domain/CommissionCascade.js";
import { CarLogo } from "../components/common/CarLogo.js";
import {
  CHIP_OFF,
  FILTER_INPUT,
  FilterField,
  FilterPanel,
} from "../components/common/FilterBar.js";
import { X } from "lucide-react";
import { useModals } from "../components/common/ModalsProvider.js";
import { useAuth } from "../hooks/useAuth.js";
import { useCanEdit } from "../hooks/useCanEdit.js";
import { useSelectedCompany } from "../hooks/useSelectedCompany.js";
import { formatMoney } from "../lib/format-fr.js";
import { trpc } from "../lib/trpc.js";
import { useT } from "../i18n/index.js";
import { Paginacao } from "../components/common/Paginacao.js";

const PAGE_SIZE = 50;
const FILTERS_STORAGE_KEY = "edsp.pagamentosTecnicos.filters";

type SortInvoiceOrder = "" | "asc" | "desc";

interface PersistedFilters {
  technicianId: number | "all";
  payerCompanyId: number | "all";
  paymentStatus: "pending" | "paid" | "cancelled" | "all";
  /** F-payouts-released-filter — gate do cliente (recebido = repasse liberado). */
  customerReceived: "all" | "received" | "waiting";
  from: string;
  to: string;
  q: string;
  sortInvoiceOrder: SortInvoiceOrder;
}

const DEFAULT_FILTERS: PersistedFilters = {
  technicianId: "all",
  payerCompanyId: "all",
  paymentStatus: "all",
  customerReceived: "all",
  from: "",
  to: "",
  q: "",
  sortInvoiceOrder: "",
};

function loadPersistedFilters(): PersistedFilters {
  if (typeof window === "undefined") return DEFAULT_FILTERS;
  // F-payouts-released-filter — o alerta do Painel ("N dossiers — repassar ao
  // técnico") abre ?liberados=1: a URL VENCE o localStorage e aterrissa já no
  // subconjunto do alerta (comissão pendente × cliente recebido), zerando os
  // demais filtros para a lista fechar com o número clicado.
  try {
    const params = new URLSearchParams(window.location.search);
    // F-payouts-tech-deeplink — clique num TÉCNICO do card do Painel:
    // ?tecnico=<id> filtra já por ele (combina com liberados/pendentes).
    const tecnicoRaw = Number(params.get("tecnico"));
    const tecnico: number | "all" =
      Number.isInteger(tecnicoRaw) && tecnicoRaw > 0 ? tecnicoRaw : "all";
    if (params.get("liberados") === "1") {
      return {
        ...DEFAULT_FILTERS,
        paymentStatus: "pending",
        customerReceived: "received",
        technicianId: tecnico,
      };
    }
    // ?pendentes=1 — chip "N dossiers — comissões · a pagar" (pendente,
    // qualquer gate do cliente).
    if (params.get("pendentes") === "1") {
      return { ...DEFAULT_FILTERS, paymentStatus: "pending", technicianId: tecnico };
    }
    if (tecnico !== "all") {
      return { ...DEFAULT_FILTERS, technicianId: tecnico };
    }
  } catch {
    // URL inacessível — segue para o localStorage.
  }
  try {
    const raw = window.localStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return DEFAULT_FILTERS;
    const parsed = JSON.parse(raw) as Partial<PersistedFilters>;
    return { ...DEFAULT_FILTERS, ...parsed };
  } catch {
    return DEFAULT_FILTERS;
  }
}

type TechnicianPaymentStatus = "pending" | "paid" | "cancelled";
type TechnicianInvoiceStatus = "pending" | "received" | "approved" | "rejected";

const PAYMENT_STATUS_TONE: Record<TechnicianPaymentStatus, string> = {
  pending: "bg-amber-500/15 text-amber-300",
  paid: "bg-lime/15 text-lime",
  cancelled: "bg-red-500/15 text-red-300",
};

const INVOICE_STATUS_TONE: Record<TechnicianInvoiceStatus, string> = {
  pending: "bg-neutral-700/40 text-neutral-300",
  received: "bg-amber-500/15 text-amber-300",
  approved: "bg-lime/15 text-lime",
  rejected: "bg-red-500/15 text-red-300",
};

export default function PagamentosTecnicos() {
  const t = useT();
  const { user } = useAuth();
  const canEdit = useCanEdit();
  const modals = useModals();
  const PAYMENT_STATUS_LABEL: Record<TechnicianPaymentStatus, string> = {
    pending: t("technicianPayments.payStatusPending"),
    paid: t("technicianPayments.payStatusPaid"),
    cancelled: t("technicianPayments.payStatusCancelled"),
  };
  const INVOICE_STATUS_LABEL: Record<TechnicianInvoiceStatus, string> = {
    pending: t("technicianPayments.invStatusPending"),
    received: t("technicianPayments.invStatusReceived"),
    approved: t("technicianPayments.invStatusApproved"),
    rejected: t("technicianPayments.invStatusRejected"),
  };
  const companiesQuery = trpc.companies.list.useQuery();
  const accessibleCompanies = (companiesQuery.data ?? []).filter((c) =>
    (user?.allowedCompanyIds ?? []).includes(c.id),
  );
  const [companyId, setCompanyId] = useSelectedCompany(accessibleCompanies);
  // Quando `companies.list` aterrissa após o primeiro render, `useState` init
  // já tomou null. Esse effect promove a primeira empresa acessível assim
  // que ela aparece — caso contrário companyId fica null para sempre sem
  // interação do usuário.

  // Filtros (persistidos em localStorage)
  const persisted = useMemo(() => loadPersistedFilters(), []);
  const [technicianId, setTechnicianId] = useState<number | "all">(persisted.technicianId);
  const [payerCompanyId, setPayerCompanyId] = useState<number | "all">(persisted.payerCompanyId);
  const [paymentStatus, setPaymentStatus] = useState<TechnicianPaymentStatus | "all">(
    persisted.paymentStatus,
  );
  const [customerReceived, setCustomerReceived] = useState<"all" | "received" | "waiting">(
    persisted.customerReceived,
  );
  const [from, setFrom] = useState(persisted.from);
  const [to, setTo] = useState(persisted.to);
  const [q, setQ] = useState(persisted.q);
  const [sortInvoiceOrder, setSortInvoiceOrder] = useState<SortInvoiceOrder>(
    persisted.sortInvoiceOrder,
  );
  const [offset, setOffset] = useState(0);

  // Persiste filtros em localStorage (debounce simples: useEffect dispara após render)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const snapshot: PersistedFilters = {
      technicianId,
      payerCompanyId,
      paymentStatus,
      customerReceived,
      from,
      to,
      q,
      sortInvoiceOrder,
    };
    try {
      window.localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(snapshot));
    } catch {
      // Quota cheia, modo privado etc — ignora silenciosamente.
    }
  }, [
    technicianId,
    payerCompanyId,
    paymentStatus,
    customerReceived,
    from,
    to,
    q,
    sortInvoiceOrder,
  ]);

  function clearFilters() {
    setTechnicianId("all");
    setPayerCompanyId("all");
    setPaymentStatus("all");
    setCustomerReceived("all");
    setFrom("");
    setTo("");
    setQ("");
    setSortInvoiceOrder("");
    setOffset(0);
  }

  // Seleção múltipla (por line.id)
  const [selected, setSelected] = useState<Set<number>>(new Set());

  // Dialogs
  const [payDialog, setPayDialog] = useState<PayDialogState | null>(null);
  const [proformaDialog, setProformaDialog] = useState(false);
  const [porForaDialog, setPorForaDialog] = useState(false);

  const techsQuery = trpc.technicians.list.useQuery(
    { companyId: companyId ?? 0, active: true, limit: 200, offset: 0 },
    { enabled: companyId !== null },
  );

  // F-pagtec-empresa (CEO 2026-09-29, BS "não aparece nada"): o técnico aberto
  // fica salvo no navegador. Trocar de empresa mantinha o técnico da OUTRA
  // (id que não existe aqui) — lista vazia, e o seletor mostrava "Todos os
  // técnicos" porque não achava a opção. Técnico fora da lista volta a "todos".
  useEffect(() => {
    if (technicianId === "all" || !techsQuery.data) return;
    if (!techsQuery.data.rows.some((r) => r.id === technicianId)) {
      setTechnicianId("all");
      setOffset(0);
    }
  }, [technicianId, techsQuery.data]);

  const filters = useMemo(
    () => ({
      companyId: companyId ?? 0,
      technicianId: technicianId === "all" ? undefined : technicianId,
      payerCompanyId: payerCompanyId === "all" ? undefined : payerCompanyId,
      technicianPaymentStatus: paymentStatus === "all" ? undefined : paymentStatus,
      customerReceived: customerReceived === "all" ? undefined : customerReceived,
      from: from || undefined,
      to: to || undefined,
      q: q || undefined,
      sortInvoiceOrder: sortInvoiceOrder === "" ? undefined : sortInvoiceOrder,
      limit: PAGE_SIZE,
      offset,
    }),
    [
      companyId,
      technicianId,
      payerCompanyId,
      paymentStatus,
      customerReceived,
      from,
      to,
      q,
      sortInvoiceOrder,
      offset,
    ],
  );

  const listQuery = trpc.serviceLines.listPagamentosTecnicos.useQuery(filters, {
    // F-pagtec-por-tecnico — com "Todos técnicos" a página mostra o resumo por
    // técnico; a lista de linhas só carrega com um técnico aberto.
    enabled: companyId !== null && technicianId !== "all",
  });
  // F-pagtec-por-tecnico (CEO 2026-09-15) — resumo agregado por técnico.
  const byTechQuery = trpc.serviceLines.pagamentosPorTecnico.useQuery(
    {
      companyId: companyId ?? 0,
      payerCompanyId: payerCompanyId === "all" ? undefined : payerCompanyId,
      from: from || undefined,
      to: to || undefined,
      q: q || undefined,
    },
    { enabled: companyId !== null && technicianId === "all" },
  );
  // F-pagtec-compacto (CEO 2026-09-27: "cards muito grandes", "KPIs da página")
  // — com um técnico aberto, o mesmo resumo do servidor, filtrado por ele:
  // os totais valem para o filtro inteiro, não só para a página carregada.
  const techSummaryQuery = trpc.serviceLines.pagamentosPorTecnico.useQuery(
    {
      companyId: companyId ?? 0,
      technicianId: technicianId === "all" ? undefined : technicianId,
      payerCompanyId: payerCompanyId === "all" ? undefined : payerCompanyId,
      from: from || undefined,
      to: to || undefined,
      q: q || undefined,
    },
    { enabled: companyId !== null && technicianId !== "all" },
  );
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // F-caixa-repasse (CEO 2026-09-28) — dinheiro recebido × adiantamentos.
  const caixaQuery = trpc.serviceLines.caixaRepasse.useQuery(
    {
      companyId: companyId ?? 0,
      payerCompanyId: payerCompanyId === "all" ? undefined : payerCompanyId,
      from: from || undefined,
      to: to || undefined,
    },
    { enabled: companyId !== null },
  );
  const [showAdiant, setShowAdiant] = useState(false);
  // F-adiantamento-tecnico (CEO 2026-10-25) — adiantamentos POR CONTA (hotel,
  // ferramentas, dinheiro do sócio…): lista + lançamento manual + cancelar.
  const [showPorConta, setShowPorConta] = useState(false);
  const [novoAdiant, setNovoAdiant] = useState(false);
  const [naTech, setNaTech] = useState<number | "">("");
  const [naValor, setNaValor] = useState("");
  const [naData, setNaData] = useState(() => new Date().toISOString().slice(0, 10));
  const [naDesc, setNaDesc] = useState("");
  const [naOrigem, setNaOrigem] = useState<"partner" | "cash">("partner");
  const [naErro, setNaErro] = useState<string | null>(null);
  const criarAdiantMut = trpc.technicianAdvances.create.useMutation({
    onSuccess: () => {
      setNovoAdiant(false);
      setNaValor("");
      setNaDesc("");
      setNaErro(null);
      void utils.serviceLines.caixaRepasse.invalidate();
      void utils.serviceLines.pagamentosPorTecnico.invalidate();
    },
    onError: (e) => setNaErro(e.message),
  });
  const cancelarAdiantMut = trpc.technicianAdvances.cancel.useMutation({
    onSuccess: () => {
      void utils.serviceLines.caixaRepasse.invalidate();
      void utils.serviceLines.pagamentosPorTecnico.invalidate();
    },
    onError: (e) => void modals.alert({ message: e.message, tone: "danger" }),
  });
  // Linhas já vistas (por id): a seleção sobrevive à troca de aba — o CEO
  // junta carros LIBERADOS e ADIANTAMENTOS (aguardando cliente) na mesma
  // proforma.
  const rowCache = useRef(new Map<number, NonNullable<typeof listQuery.data>["rows"][number]>());
  const utils = trpc.useUtils();

  // F10.5R — quais linhas já estão cobertas por proforma ativa.
  const pageLineIds = useMemo(
    () => (listQuery.data?.rows ?? []).map((r) => r.id),
    [listQuery.data?.rows],
  );
  const activeProformasQuery = trpc.proformaRequests.linesInActiveProforma.useQuery(
    { companyId: companyId ?? 0, lineIds: pageLineIds },
    { enabled: companyId !== null && pageLineIds.length > 0 },
  );
  const linesInProforma = activeProformasQuery.data ?? {};

  // Quando os dados aterrissam e revelam que uma linha selecionada já está
  // em proforma ativa, remove ela do `selected` para evitar submit com linha
  // bloqueada. Cobre o caso "selecionei, alguém criou proforma em paralelo".
  useEffect(() => {
    if (Object.keys(linesInProforma).length === 0) return;
    setSelected((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const id of prev) {
        if (linesInProforma[id]) {
          next.delete(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [linesInProforma]);

  // ⚠ Todos os hooks (useMemo/useEffect/useState/use*) ficam ACIMA dos early
  // returns para preservar a regra dos hooks. Quando user pula de null →
  // logado, a contagem de hooks chamada não pode mudar entre renders.
  const data = listQuery.data;
  for (const r of data?.rows ?? []) rowCache.current.set(r.id, r);

  if (!user) return null;
  if (accessibleCompanies.length === 0) {
    return (
      <Shell>
        <p className="text-sm text-neutral-400">{t("technicianPayments.noCompanyAccess")}</p>
      </Shell>
    );
  }

  const total = data?.total ?? 0;

  // Lines selecionadas (a partir do dataset atual)
  const selectedRows = [...selected]
    .map((id) => rowCache.current.get(id))
    .filter((r): r is NonNullable<typeof r> => r != null);
  const selectedTechnicianId = selectedRows.length > 0 ? selectedRows[0]!.technicianId : null;
  const selectedTechnicianName = selectedRows.length > 0 ? selectedRows[0]!.technician.name : "";
  // CEO 2026-10-26 — "Registrar pago por fora" é só do admin da empresa.
  const isAdmin =
    user?.isSuperAdmin === true ||
    (user?.memberships ?? []).some(
      (m) => m.companyId === companyId && m.active && m.role === "admin",
    );
  const selectedAllSameTechnician = selectedRows.every(
    (r) => r.technicianId === selectedTechnicianId,
  );
  const selectedSum = selectedRows.reduce(
    (acc, r) => acc + Number(r.technicianAmount ?? r.technicianCommissionAmount ?? 0),
    0,
  );
  // F-proforma-split — técnico com intermediário: a proforma vai para o
  // intermediário e soma técnico + intermediário (a barra mostrava só o técnico).
  // F-caixa-repasse — quanto da seleção é ADIANTAMENTO (cliente ainda não pagou).
  const selectedAdiant = selectedRows
    .filter((r) => r.dossier.customerInvoiceStatus !== "paid")
    .reduce((acc, r) => acc + Number(r.technicianAmount ?? r.technicianCommissionAmount ?? 0), 0);
  const selectedLiberado = selectedSum - selectedAdiant;
  const disponivel = caixaQuery.data?.disponivel ?? null;
  const selectedInterm = selectedRows.reduce(
    (acc, r) => acc + Number(r.intermediaryAmount ?? 0),
    0,
  );
  // CEO 2026-10-18: o intermediário mora no PAR técnico × cliente (o do
  // cadastro do técnico é legado e vence quando existe) — mesma regra do
  // servidor (proforma-flow.ts). O diálogo pergunta ao servidor.
  const selectedIntermId =
    selectedRows.length > 0
      ? (selectedRows[0]!.technician.intermediaryCompanyId ??
        selectedRows[0]!.pair?.intermediaryCompanyId ??
        null)
      : null;
  const selectedHasInterm = selectedIntermId != null;
  const intermName = selectedHasInterm
    ? (companiesQuery.data?.find((c) => c.id === selectedIntermId)?.name ?? null)
    : null;

  function toggleSelect(lineId: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  }

  function clearSelection() {
    setSelected(new Set());
  }

  // F-pagtec-compacto — abas da tela do técnico = combinações dos filtros
  // existentes (situação do repasse × recebimento do cliente).
  type Stage = "released" | "waiting" | "paid" | "all";
  const stage: Stage | null =
    paymentStatus === "pending" && customerReceived === "received"
      ? "released"
      : paymentStatus === "pending" && customerReceived === "waiting"
        ? "waiting"
        : paymentStatus === "paid" && customerReceived === "all"
          ? "paid"
          : paymentStatus === "all" && customerReceived === "all"
            ? "all"
            : null;
  function setStage(s: Stage) {
    setPaymentStatus(
      s === "released" || s === "waiting" ? "pending" : s === "paid" ? "paid" : "all",
    );
    setCustomerReceived(s === "released" ? "received" : s === "waiting" ? "waiting" : "all");
    setOffset(0);
    setExpanded(new Set());
    // F-caixa-repasse — NÃO limpa a seleção: liberados + adiantamentos juntos.
  }
  function toggleExpanded(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }
  const dataCurta = (iso: string | null | undefined) =>
    iso
      ? new Date(iso).toLocaleDateString("fr-FR", {
          day: "2-digit",
          month: "2-digit",
          year: "2-digit",
        })
      : null;

  // F-pagtec-grouped (pedido do CEO 2026-09-11) — UMA linha por carro×técnico:
  // DSP + Montagem do mesmo dossier deixam de aparecer como 2 linhas; os
  // serviços viram chips na MESMA linha e os valores são SOMADOS. Por baixo o
  // pagamento continua POR LINHA (o checkbox do grupo marca todas as linhas
  // livres; com mais de um serviço, o botão Pagar aparece por serviço).
  type Row = NonNullable<typeof data>["rows"][number];
  function groupRows(rows: readonly Row[]): Row[][] {
    const map = new Map<string, Row[]>();
    for (const r of rows) {
      const k = `${r.dossierId}:${r.technicianId}`;
      const arr = map.get(k);
      if (arr) arr.push(r);
      else map.set(k, [r]);
    }
    return [...map.values()];
  }

  function openPayDialog(row: NonNullable<typeof data>["rows"][number]) {
    const ti = row.technicianInvoice;
    if (!ti) {
      void modals.alert({
        message: t("technicianPayments.noInvoiceAlert"),
      });
      return;
    }
    const totalAmt = Number(ti.totalAmount);
    const paidAmt = Number(ti.paidTotal);
    const open = totalAmt - paidAmt;
    setPayDialog({
      lineId: row.id,
      technicianInvoiceId: ti.id,
      technicianName: row.technician.name,
      invoiceNumber: ti.invoiceNumberExternal,
      defaultAmount: open > 0 ? open : 0,
    });
  }

  return (
    <Shell>
      <FilterPanel testId="pagamentosTecnicos-filter-panel">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          <FilterField label={t("technicianPayments.filterCompany")}>
            <select
              value={companyId ?? ""}
              onChange={(e) => {
                setCompanyId(Number(e.target.value));
                setOffset(0);
                clearSelection();
              }}
              className={FILTER_INPUT + " w-full"}
            >
              {accessibleCompanies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </FilterField>
          <FilterField label={t("technicianPayments.filterSearch")} className="sm:col-span-2">
            <input
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setOffset(0);
              }}
              placeholder={t("technicianPayments.searchPlaceholder")}
              className={FILTER_INPUT + " w-full"}
            />
          </FilterField>
          <FilterField label={t("technicianPayments.filterTechnician")}>
            <select
              value={technicianId === "all" ? "all" : String(technicianId)}
              onChange={(e) => {
                const v = e.target.value;
                setTechnicianId(v === "all" ? "all" : Number(v));
                setOffset(0);
                clearSelection();
              }}
              className={FILTER_INPUT + " w-full"}
            >
              <option value="all">{t("technicianPayments.allTechnicians")}</option>
              {(techsQuery.data?.rows ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </FilterField>
          <FilterField label={t("technicianPayments.filterPayer")}>
            <select
              value={payerCompanyId === "all" ? "all" : String(payerCompanyId)}
              onChange={(e) => {
                const v = e.target.value;
                setPayerCompanyId(v === "all" ? "all" : Number(v));
                setOffset(0);
                clearSelection();
              }}
              className={FILTER_INPUT + " w-full"}
            >
              <option value="all">{t("technicianPayments.allPayers")}</option>
              {accessibleCompanies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </FilterField>
          <FilterField label={t("technicianPayments.filterPaymentStatus")}>
            <select
              value={paymentStatus}
              onChange={(e) => {
                setPaymentStatus(e.target.value as TechnicianPaymentStatus | "all");
                setOffset(0);
                clearSelection();
              }}
              className={FILTER_INPUT + " w-full"}
            >
              <option value="all">{t("technicianPayments.allPaymentStatuses")}</option>
              <option value="pending">{t("technicianPayments.optPending")}</option>
              <option value="paid">{t("technicianPayments.optPaid")}</option>
              <option value="cancelled">{t("technicianPayments.optCancelled")}</option>
            </select>
          </FilterField>
          {/* F-payouts-released-filter — gate do cliente: mesmo critério do
              alerta "repassar ao técnico" do Painel (fatura do cliente paga). */}
          <FilterField label={t("technicianPayments.filterCustomerReceived")}>
            <select
              value={customerReceived}
              onChange={(e) => {
                setCustomerReceived(e.target.value as "all" | "received" | "waiting");
                setOffset(0);
                // F-caixa-repasse — mantém a seleção (liberados + adiantamentos).
              }}
              className={FILTER_INPUT + " w-full"}
            >
              <option value="all">{t("technicianPayments.custRecAll")}</option>
              <option value="received">{t("technicianPayments.custRecReceived")}</option>
              <option value="waiting">{t("technicianPayments.custRecWaiting")}</option>
            </select>
          </FilterField>
        </div>

        <div className="flex flex-wrap items-end gap-x-4 gap-y-3 border-t border-[var(--divider)] pt-4">
          <FilterField label={t("technicianPayments.filterPeriod")}>
            <div className="flex items-center gap-1.5">
              <input
                type="date"
                value={from}
                onChange={(e) => {
                  setFrom(e.target.value);
                  setOffset(0);
                }}
                className={FILTER_INPUT + " w-full sm:w-36"}
                title={t("technicianPayments.dateFrom")}
              />
              <span style={{ color: "var(--text-muted)" }}>→</span>
              <input
                type="date"
                value={to}
                onChange={(e) => {
                  setTo(e.target.value);
                  setOffset(0);
                }}
                className={FILTER_INPUT + " w-full sm:w-36"}
                title={t("technicianPayments.dateTo")}
              />
            </div>
          </FilterField>
          <FilterField label={t("technicianPayments.filterSortBy")}>
            <select
              value={sortInvoiceOrder}
              onChange={(e) => {
                setSortInvoiceOrder(e.target.value as SortInvoiceOrder);
                setOffset(0);
              }}
              className={FILTER_INPUT + " w-full sm:w-auto"}
              title={t("technicianPayments.sortTitle")}
            >
              <option value="">{t("technicianPayments.sortDefault")}</option>
              <option value="asc">{t("technicianPayments.sortInvoiceAsc")}</option>
              <option value="desc">{t("technicianPayments.sortInvoiceDesc")}</option>
            </select>
          </FilterField>
          <div className="flex-1" />
          <button
            onClick={clearFilters}
            className={CHIP_OFF}
            title={t("technicianPayments.clearFiltersTitle")}
          >
            <X size={15} aria-hidden="true" />
            {t("technicianPayments.clearFilters")}
          </button>
        </div>
      </FilterPanel>

      {/* F-caixa-repasse (CEO 2026-09-28) — o dinheiro recebido é um só: o CEO
          adianta carros de um técnico com o caixa de outro. Proteção contra
          pagar 2× continua por carro (1 proforma ativa; pago não volta). */}
      {caixaQuery.data && (
        <div
          className="rounded-lg border border-neutral-800 bg-neutral-900 px-4 py-3"
          data-testid="caixa-repasse"
        >
          <div className="flex flex-wrap items-end gap-x-8 gap-y-2">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-neutral-500">
                {t("technicianPayments.cxReleasedFree")}
              </div>
              <div className="font-mono text-base font-semibold tabular-nums">
                {formatMoney(caixaQuery.data.liberadoLivre, { decimals: 2 })}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-neutral-500">
                {t("technicianPayments.cxInProforma")}
              </div>
              <div className="font-mono text-sm tabular-nums text-neutral-400">
                {formatMoney(caixaQuery.data.liberadoEmProforma, { decimals: 2 })}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-neutral-500">
                − {t("technicianPayments.cxAdvances")}
              </div>
              <button
                onClick={() => setShowAdiant((v) => !v)}
                className="font-mono text-sm tabular-nums text-amber-300 hover:underline"
                title={t("technicianPayments.cxAdvancesTitle")}
              >
                {formatMoney(caixaQuery.data.adiantamentosCarros, { decimals: 2 })} ·{" "}
                {caixaQuery.data.adiantamentosLista.length} {t("technicianPayments.byTechCars")}{" "}
                {showAdiant ? "▾" : "▸"}
              </button>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-neutral-500">
                − {t("technicianPayments.cxAdvancesAccount")}
              </div>
              <button
                onClick={() => setShowPorConta((v) => !v)}
                className="font-mono text-sm tabular-nums text-amber-300 hover:underline"
                title={t("technicianPayments.cxAdvancesAccountTitle")}
                data-testid="cx-adv-account"
              >
                {formatMoney(caixaQuery.data.adiantamentosPorConta, { decimals: 2 })} ·{" "}
                {
                  caixaQuery.data.adiantamentosPorContaLista.filter((a) => a.origin !== "partner")
                    .length
                }{" "}
                {showPorConta ? "▾" : "▸"}
              </button>
            </div>
            <div className="ml-auto text-right">
              <div className="text-[10px] uppercase tracking-wider text-neutral-500">
                = {t("technicianPayments.cxAvailable")}
              </div>
              <div
                className="font-mono text-lg font-bold tabular-nums"
                style={{ color: caixaQuery.data.disponivel >= 0 ? "#16A34A" : "#DC2626" }}
              >
                {formatMoney(caixaQuery.data.disponivel, { decimals: 2 })}
              </div>
            </div>
            {/* CEO 2026-10-25 — saldo do extrato (o que o banco informou no
                último arquivo importado) ao lado do Disponível. */}
            <div
              className="w-full border-t border-neutral-800 pt-2 text-right sm:w-auto sm:border-l sm:border-t-0 sm:pl-6 sm:pt-0"
              data-testid="cx-saldo-extrato"
              title={t("technicianPayments.cxBankBalanceTitle")}
            >
              <div className="text-[10px] uppercase tracking-wider text-neutral-500">
                {t("technicianPayments.cxBankBalance")}
              </div>
              {caixaQuery.data.saldosExtrato.filter((s) => s.amount != null).length === 0 ? (
                <div className="max-w-[14rem] text-[11px] text-neutral-500">
                  {t("technicianPayments.cxBankBalanceNone")}
                </div>
              ) : (
                caixaQuery.data.saldosExtrato
                  .filter((s) => s.amount != null)
                  .map((s) => (
                    <div key={s.id} className="whitespace-nowrap">
                      <span className="font-mono text-lg font-bold tabular-nums">
                        {formatMoney(s.amount ?? 0, { decimals: 2 })}
                      </span>
                      <span className="ml-1.5 text-[11px] text-neutral-500">
                        {s.label}
                        {s.at
                          ? ` · ${new Date(`${s.at}T00:00:00`).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" })}`
                          : ""}
                      </span>
                    </div>
                  ))
              )}
            </div>
          </div>
          <p className="mt-1 text-[11px] text-neutral-500">{t("technicianPayments.cxHint")}</p>
          {(() => {
            const banco = caixaQuery.data.saldosExtrato
              .filter((s) => s.amount != null)
              .reduce((a, s) => a + (s.amount ?? 0), 0);
            const temSaldo = caixaQuery.data.saldosExtrato.some((s) => s.amount != null);
            const passa = caixaQuery.data.disponivel - banco;
            return temSaldo && passa > 0.005 ? (
              <p className="mt-1 text-[11px] text-amber-300" data-testid="cx-over-bank">
                ⚠ {t("technicianPayments.cxOverBankPre")}{" "}
                <b className="font-mono tabular-nums">{formatMoney(passa, { decimals: 2 })}</b>.{" "}
                {t("technicianPayments.cxOverBankSuf")}
              </p>
            ) : null;
          })()}
          {showPorConta && (
            <div
              className="mt-3 rounded-md border border-neutral-800"
              data-testid="cx-adv-account-list"
            >
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-neutral-800 px-3 py-2">
                <p className="text-[11px] text-neutral-500">
                  {t("technicianPayments.advAccountHint")}
                  {caixaQuery.data.adiantamentosPorContaSocio > 0 && (
                    <span className="ml-1 text-amber-300" data-testid="adv-partner-note">
                      {t("technicianPayments.advPartnerNote")}{" "}
                      {formatMoney(caixaQuery.data.adiantamentosPorContaSocio, { decimals: 2 })}.
                    </span>
                  )}
                </p>
                {canEdit && (
                  <button
                    onClick={() => {
                      setNovoAdiant((v) => !v);
                      setNaErro(null);
                    }}
                    className="rounded-md border border-neutral-700 px-2.5 py-1 text-xs text-neutral-200 hover:border-neutral-500"
                    data-testid="adv-new-btn"
                  >
                    + {t("technicianPayments.advNew")}
                  </button>
                )}
              </div>
              {novoAdiant && canEdit && (
                <form
                  className="grid grid-cols-2 gap-2 border-b border-neutral-800 bg-neutral-950/40 px-3 py-3 text-xs md:grid-cols-6"
                  data-testid="adv-new-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const valor = Number(naValor.replace(",", "."));
                    if (naTech === "" || !(valor > 0) || naDesc.trim().length < 2 || !companyId) {
                      setNaErro(t("technicianPayments.advFormIncomplete"));
                      return;
                    }
                    criarAdiantMut.mutate({
                      companyId,
                      technicianId: naTech,
                      amount: Math.round(valor * 100) / 100,
                      occurredAt: naData,
                      description: naDesc.trim(),
                      origin: naOrigem,
                    });
                  }}
                >
                  <label className="col-span-2 flex flex-col gap-1">
                    <span className="text-[10px] uppercase tracking-wider text-neutral-500">
                      {t("technicianPayments.colTechnician")}
                    </span>
                    <select
                      value={naTech}
                      onChange={(e) =>
                        setNaTech(e.target.value === "" ? "" : Number(e.target.value))
                      }
                      className="rounded-md border border-neutral-700 bg-neutral-950 px-2 py-1.5 text-neutral-100"
                      data-testid="adv-tech"
                    >
                      <option value="">—</option>
                      {(techsQuery.data?.rows ?? []).map((tech) => (
                        <option key={tech.id} value={tech.id}>
                          {tech.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-[10px] uppercase tracking-wider text-neutral-500">
                      {t("technicianPayments.advAmount")}
                    </span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={naValor}
                      onChange={(e) => setNaValor(e.target.value)}
                      placeholder="0,00"
                      className="rounded-md border border-neutral-700 bg-neutral-950 px-2 py-1.5 font-mono text-neutral-100"
                      data-testid="adv-amount"
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-[10px] uppercase tracking-wider text-neutral-500">
                      {t("technicianPayments.advDate")}
                    </span>
                    <input
                      type="date"
                      value={naData}
                      onChange={(e) => setNaData(e.target.value)}
                      className="rounded-md border border-neutral-700 bg-neutral-950 px-2 py-1.5 text-neutral-100"
                      data-testid="adv-date"
                    />
                  </label>
                  <label className="col-span-2 flex flex-col gap-1">
                    <span className="text-[10px] uppercase tracking-wider text-neutral-500">
                      {t("technicianPayments.advOrigin")}
                    </span>
                    <select
                      value={naOrigem}
                      onChange={(e) => setNaOrigem(e.target.value as "partner" | "cash")}
                      className="rounded-md border border-neutral-700 bg-neutral-950 px-2 py-1.5 text-neutral-100"
                      data-testid="adv-origin"
                    >
                      <option value="partner">{t("technicianPayments.advOriginPartner")}</option>
                      <option value="cash">{t("technicianPayments.advOriginCash")}</option>
                    </select>
                  </label>
                  <label className="col-span-2 flex flex-col gap-1 md:col-span-5">
                    <span className="text-[10px] uppercase tracking-wider text-neutral-500">
                      {t("technicianPayments.advDescription")}
                    </span>
                    <input
                      type="text"
                      value={naDesc}
                      onChange={(e) => setNaDesc(e.target.value)}
                      maxLength={300}
                      placeholder={t("technicianPayments.advDescriptionHint")}
                      className="rounded-md border border-neutral-700 bg-neutral-950 px-2 py-1.5 text-neutral-100"
                      data-testid="adv-desc"
                    />
                  </label>
                  <div className="col-span-2 flex items-end gap-2 md:col-span-1">
                    <button
                      type="submit"
                      disabled={criarAdiantMut.isPending}
                      className="w-full rounded-md bg-lime px-3 py-1.5 font-semibold text-black hover:brightness-110 disabled:opacity-50"
                      data-testid="adv-save"
                    >
                      {t("technicianPayments.advSave")}
                    </button>
                  </div>
                  {naErro && (
                    <p
                      className="col-span-2 text-[11px] text-red-400 md:col-span-6"
                      data-testid="adv-error"
                    >
                      {naErro}
                    </p>
                  )}
                  <p className="col-span-2 text-[11px] text-neutral-500 md:col-span-6">
                    {t("technicianPayments.advBankHint")}
                  </p>
                </form>
              )}
              {caixaQuery.data.adiantamentosPorContaLista.length === 0 ? (
                <p className="p-3 text-xs text-neutral-500">
                  {t("technicianPayments.advNoneOpen")}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-neutral-950/50 text-neutral-500">
                      <tr>
                        <th className="px-3 py-1.5 text-left">
                          {t("technicianPayments.colTechnician")}
                        </th>
                        <th className="px-3 py-1.5 text-left">{t("technicianPayments.advDate")}</th>
                        <th className="px-3 py-1.5 text-left">
                          {t("technicianPayments.advDescription")}
                        </th>
                        <th className="px-3 py-1.5 text-left">
                          {t("technicianPayments.advOrigin")}
                        </th>
                        <th className="px-3 py-1.5 text-right">
                          {t("technicianPayments.advAmount")}
                        </th>
                        {canEdit && <th className="px-3 py-1.5" />}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-800">
                      {caixaQuery.data.adiantamentosPorContaLista.map((a) => (
                        <tr key={a.id} data-testid={`adv-row-${a.id}`}>
                          <td className="px-3 py-1.5">{a.technicianName}</td>
                          <td className="px-3 py-1.5 font-mono whitespace-nowrap">
                            {a.occurredAt}
                          </td>
                          <td className="px-3 py-1.5">{a.description}</td>
                          <td className="px-3 py-1.5 text-neutral-400">
                            {a.origin === "company_bank"
                              ? t("technicianPayments.advOriginBank")
                              : a.origin === "partner"
                                ? t("technicianPayments.advOriginPartner")
                                : a.origin === "devis_adjustment"
                                  ? t("technicianPayments.advOriginAdjustment")
                                  : t("technicianPayments.advOriginCash")}
                          </td>
                          <td className="px-3 py-1.5 text-right font-mono tabular-nums whitespace-nowrap">
                            {formatMoney(a.amount, { decimals: 2 })}
                          </td>
                          {canEdit && (
                            <td className="px-3 py-1.5 text-right">
                              <button
                                onClick={async () => {
                                  const motivo = await modals.promptReason({
                                    title: t("technicianPayments.advCancelTitle"),
                                    message: t("technicianPayments.advCancelMsg"),
                                  });
                                  if (!motivo || !companyId) return;
                                  cancelarAdiantMut.mutate({ companyId, id: a.id, reason: motivo });
                                }}
                                className="text-[11px] text-neutral-500 hover:text-red-400"
                                data-testid={`adv-cancel-${a.id}`}
                              >
                                {t("technicianPayments.advCancel")}
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          {showAdiant && (
            <div className="mt-3 overflow-x-auto rounded-md border border-neutral-800">
              {caixaQuery.data.adiantamentosLista.length === 0 ? (
                <p className="p-3 text-xs text-neutral-500">
                  {t("technicianPayments.cxNoAdvances")}
                </p>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-neutral-950/50 text-neutral-500">
                    <tr>
                      <th className="px-3 py-1.5 text-left">
                        {t("technicianPayments.colTechnician")}
                      </th>
                      <th className="px-3 py-1.5 text-left">
                        {t("technicianPayments.colVehicle")}
                      </th>
                      <th className="px-3 py-1.5 text-left">
                        {t("technicianPayments.colCustomer")}
                      </th>
                      <th className="px-3 py-1.5 text-left">
                        {t("technicianPayments.colCustomerInvoice")}
                      </th>
                      <th className="px-3 py-1.5 text-right">
                        {t("technicianPayments.cxColAdvanced")}
                      </th>
                      <th className="px-3 py-1.5 text-left">
                        {t("technicianPayments.colSituation")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-800">
                    {caixaQuery.data.adiantamentosLista.map((a) => (
                      <tr key={`${a.dossierId}:${a.technicianId}`}>
                        <td className="px-3 py-1.5">{a.technicianName}</td>
                        <td className="px-3 py-1.5">
                          <Link
                            href={`/dossiers/${a.dossierId}`}
                            className="font-mono text-lime hover:underline"
                          >
                            {a.plate ?? a.vin ?? a.dossierCode}
                          </Link>
                          {a.make && <span className="ml-1 text-neutral-500">{a.make}</span>}
                        </td>
                        <td className="px-3 py-1.5">
                          {a.customerName}
                          {a.endCustomerName && (
                            <span className="text-neutral-500"> ↳ {a.endCustomerName}</span>
                          )}
                        </td>
                        <td className="px-3 py-1.5 font-mono">{a.invoiceNumber ?? "—"}</td>
                        <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                          {formatMoney(a.amount, { decimals: 2 })}
                        </td>
                        <td className="px-3 py-1.5">
                          {a.paid ? (
                            <span className="badge b-warn">
                              {t("technicianPayments.cxPaidBefore")}
                            </span>
                          ) : (
                            <span className="badge b-info">🔒 {a.proforma}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}

      {/* F-pagtec-por-tecnico — visão padrão: resumo POR TÉCNICO; clicar no
          técnico abre a lista de linhas dele (a visão antiga, filtrada). */}
      {technicianId === "all" && (
        <>
          <div className="rounded-lg border border-neutral-800 bg-neutral-900 overflow-x-auto">
            {byTechQuery.isLoading && (
              <p className="p-4 text-sm text-neutral-500">{t("technicianPayments.loading")}</p>
            )}
            {byTechQuery.error && (
              <p className="p-4 text-sm text-red-400">{byTechQuery.error.message}</p>
            )}
            {byTechQuery.data && byTechQuery.data.length === 0 && (
              <p className="p-4 text-sm text-neutral-500">{t("technicianPayments.emptyState")}</p>
            )}
            {byTechQuery.data && byTechQuery.data.length > 0 && (
              <table className="w-full text-sm">
                <thead className="bg-neutral-950/50 text-xs uppercase tracking-wider text-neutral-500">
                  <tr>
                    <th className="text-left px-3 py-2">{t("technicianPayments.colTechnician")}</th>
                    <th className="text-right px-3 py-2 w-40 font-mono">
                      {t("technicianPayments.byTechColReleased")}
                    </th>
                    <th className="text-right px-3 py-2 w-40 font-mono">
                      {t("technicianPayments.byTechColWaiting")}
                    </th>
                    <th className="text-right px-3 py-2 w-36 font-mono">
                      {t("technicianPayments.byTechColPaid")}
                    </th>
                    <th className="text-right px-3 py-2 w-32 font-mono">
                      {t("technicianPayments.byTechColAdvanced")}
                    </th>
                    <th className="text-right px-3 py-2 w-44">
                      {t("technicianPayments.byTechColPending")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-800">
                  {byTechQuery.data.map((r) => (
                    <tr
                      key={r.technicianId}
                      className="cursor-pointer hover:bg-neutral-800/40"
                      onClick={() => {
                        setTechnicianId(r.technicianId);
                        setStage(r.releasedDossiers > 0 ? "released" : "all");
                      }}
                    >
                      <td className="px-3 py-2.5">
                        <span className="font-medium text-lime hover:underline">
                          {r.technicianName}
                        </span>
                        {r.noCommissionDossiers > 0 && (
                          <span
                            className="badge b-dang ml-2"
                            title={t("technicianPayments.noCommissionTitle")}
                          >
                            ⚠ {r.noCommissionDossiers} {t("technicianPayments.noCommissionTag")}
                          </span>
                        )}
                      </td>
                      <td
                        className="px-3 py-2.5 text-right font-mono tabular-nums font-semibold"
                        style={{ color: r.releasedAmount > 0 ? "#D97706" : undefined }}
                      >
                        {formatMoney(r.releasedAmount, { decimals: 2 })}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono tabular-nums text-neutral-400">
                        {formatMoney(r.waitingAmount, { decimals: 2 })}
                      </td>
                      <td
                        className="px-3 py-2.5 text-right font-mono tabular-nums"
                        style={{ color: r.paidAmount > 0 ? "#16A34A" : undefined }}
                      >
                        {formatMoney(r.paidAmount, { decimals: 2 })}
                      </td>
                      <td
                        className="px-3 py-2.5 text-right font-mono tabular-nums"
                        style={{ color: r.advancesOpen > 0 ? "#B45309" : undefined }}
                        title={t("technicianPayments.byTechColAdvancedTitle")}
                      >
                        {r.advancesOpen > 0 ? formatMoney(r.advancesOpen, { decimals: 2 }) : "—"}
                      </td>
                      <td className="px-3 py-2.5 text-right text-xs text-neutral-400">
                        {r.releasedDossiers} {t("technicianPayments.stageReleasedShort")} ·{" "}
                        {r.waitingDossiers} {t("technicianPayments.stageWaitingShort")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <p className="text-[11px] text-neutral-500 -mt-2">{t("technicianPayments.byTechHint")}</p>
        </>
      )}

      {technicianId !== "all" && (
        <button
          onClick={() => {
            setTechnicianId("all");
            setOffset(0);
            clearSelection();
          }}
          className="self-start rounded-md border border-neutral-700 hover:border-neutral-500 px-3 py-1.5 text-xs text-neutral-300"
        >
          ← {t("technicianPayments.backAllTechs")}
        </button>
      )}

      {/* F-pagtec-compacto (CEO 2026-09-27) — técnico aberto: abas compactas
          com os totais do filtro inteiro; 1 linha curta por carro e o resto
          (repartição, fatura do técnico, pagar) no clique. */}
      {technicianId !== "all" && (
        <>
          {(() => {
            const r = techSummaryQuery.data?.[0];
            const abas: Array<{
              s: Stage;
              label: string;
              valor: number | null;
              carros: number | null;
              cor?: string;
            }> = [
              {
                s: "released",
                label: t("technicianPayments.stageReleased"),
                valor: r?.releasedAmount ?? 0,
                carros: r?.releasedDossiers ?? 0,
                cor: "#D97706",
              },
              {
                s: "waiting",
                label: t("technicianPayments.stageWaiting"),
                valor: r?.waitingAmount ?? 0,
                carros: r?.waitingDossiers ?? 0,
              },
              {
                s: "paid",
                label: t("technicianPayments.optPaid"),
                valor: r?.paidAmount ?? 0,
                carros: r?.paidDossiers ?? 0,
                cor: "#16A34A",
              },
              { s: "all", label: t("technicianPayments.stageAll"), valor: null, carros: null },
            ];
            return (
              <div className="flex flex-col gap-2">
                <div className="flex flex-wrap gap-2" role="tablist">
                  {abas.map((a) => {
                    const ativa = stage === a.s;
                    return (
                      <button
                        key={a.s}
                        role="tab"
                        aria-selected={ativa}
                        onClick={() => setStage(a.s)}
                        data-testid={`pagtec-stage-${a.s}`}
                        className={`rounded-md border px-3 py-1.5 text-left text-xs ${
                          ativa
                            ? "border-lime bg-lime/10"
                            : "border-neutral-800 hover:border-neutral-600"
                        }`}
                      >
                        <span className="block text-[10px] uppercase tracking-wider text-neutral-500">
                          {a.label}
                        </span>
                        {a.valor != null && (
                          <span
                            className="font-mono tabular-nums text-sm font-semibold"
                            style={{ color: a.valor > 0 ? a.cor : undefined }}
                          >
                            {formatMoney(a.valor, { decimals: 2 })}
                          </span>
                        )}
                        {a.carros != null && (
                          <span className="ml-1.5 text-[11px] text-neutral-500">
                            · {a.carros} {t("technicianPayments.byTechCars")}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
                {r && r.noCommissionDossiers > 0 && (
                  <p className="text-xs text-red-400">
                    ⚠ {r.noCommissionDossiers} {t("technicianPayments.noCommissionWarn")}
                  </p>
                )}
                <p className="text-[11px] text-neutral-500">
                  {t("technicianPayments.detailsHint")}
                </p>
              </div>
            );
          })()}

          <div className="rounded-lg border border-neutral-800 bg-neutral-900 overflow-x-auto">
            {listQuery.isLoading && (
              <p className="p-4 text-sm text-neutral-500">{t("technicianPayments.loading")}</p>
            )}
            {listQuery.error && (
              <p className="p-4 text-sm text-red-400">{listQuery.error.message}</p>
            )}
            {data && data.rows.length === 0 && (
              <p className="p-4 text-sm text-neutral-500">{t("technicianPayments.emptyState")}</p>
            )}
            {data && data.rows.length > 0 && (
              <table className="w-full text-sm">
                <thead className="bg-neutral-950/50 text-xs uppercase tracking-wider text-neutral-500">
                  <tr>
                    <th className="w-10 px-3 py-2">
                      <input
                        type="checkbox"
                        checked={
                          data.rows.filter((r) => !linesInProforma[r.id]).length > 0 &&
                          data.rows
                            .filter((r) => !linesInProforma[r.id])
                            .every((r) => selected.has(r.id))
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            // Seleciona apenas linhas livres (não em proforma ativa)
                            // (soma à seleção de outras abas — F-caixa-repasse)
                            setSelected(
                              new Set([
                                ...selected,
                                ...data.rows.filter((r) => !linesInProforma[r.id]).map((r) => r.id),
                              ]),
                            );
                          } else {
                            const visiveis = new Set(data.rows.map((r) => r.id));
                            setSelected(new Set([...selected].filter((id) => !visiveis.has(id))));
                          }
                        }}
                        title={t("technicianPayments.selectFreeLines")}
                      />
                    </th>
                    <th className="text-left px-3 py-2">{t("technicianPayments.colVehicle")}</th>
                    <th className="text-left px-3 py-2">{t("technicianPayments.colCustomer")}</th>
                    <th className="text-left px-3 py-2">
                      {t("technicianPayments.colCustomerInvoice")}
                    </th>
                    <th className="text-right px-3 py-2 w-32 font-mono">
                      {t("technicianPayments.colToPay")}
                    </th>
                    <th className="text-left px-3 py-2 w-40">
                      {t("technicianPayments.colSituation")}
                    </th>
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-800">
                  {groupRows(data.rows).map((g) => {
                    const first = g[0]!;
                    const key = `${first.dossierId}:${first.technicianId}`;
                    const aberto = expanded.has(key);
                    // F-peca-paga-uma-vez — livre = peça a pagar fora de proforma
                    // ativa (o carro pode estar na proforma de outro técnico).
                    const freeIds = g
                      .filter(
                        (r) => !linesInProforma[r.id] && r.technicianPaymentStatus === "pending",
                      )
                      .map((r) => r.id);
                    const allLocked = freeIds.length === 0;
                    const groupSelected =
                      freeIds.length > 0 && freeIds.every((id) => selected.has(id));
                    const invoices = new Map<number, NonNullable<Row["technicianInvoice"]>>();
                    for (const r of g) {
                      if (r.technicianInvoice)
                        invoices.set(r.technicianInvoice.id, r.technicianInvoice);
                    }
                    const semComissao = g.every(
                      (r) => r.technicianAmount == null && r.technicianCommissionAmount == null,
                    );
                    const toPay = g.reduce(
                      (a, r) => a + Number(r.technicianAmount ?? r.technicianCommissionAmount ?? 0),
                      0,
                    );
                    const paid = [...invoices.values()].reduce(
                      (a, ti) => a + Number(ti.paidTotal),
                      0,
                    );
                    const attach = [...invoices.values()].reduce(
                      (a, ti) => a + ti.attachmentCount,
                      0,
                    );
                    const invStatusOf = (r: Row): TechnicianInvoiceStatus =>
                      r.technicianInvoice?.docStatus === "approved"
                        ? "approved"
                        : r.technicianInvoice?.docStatus === "received"
                          ? "received"
                          : r.technicianInvoice?.docStatus === "rejected"
                            ? "rejected"
                            : "pending";
                    const invStatuses = [...new Set(g.map(invStatusOf))];
                    const sum = (f: (r: Row) => number | string | null | undefined) =>
                      g.reduce((a, r) => a + Number(f(r) ?? 0), 0);
                    // % só é exibido quando é o MESMO em todas as linhas do carro.
                    const pctIfUniform = (f: (r: Row) => number | string | null | undefined) =>
                      g.every((r) => Number(f(r) ?? -1) === Number(f(first) ?? -1))
                        ? f(first)
                        : undefined;
                    const serviceLabel = (st: string) =>
                      st === "dsp"
                        ? "DSP"
                        : st === "peinture"
                          ? t("dossierDetail.serviceTypePeinture")
                          : st === "pieces"
                            ? t("dossierDetail.serviceTypePieces")
                            : t("dossierDetail.serviceTypeMontage");
                    const clientePagou = first.dossier.customerInvoiceStatus === "paid";
                    const tudoPago = g.every((r) => r.technicianPaymentStatus === "paid");
                    const cancelado = g.every((r) => r.technicianPaymentStatus === "cancelled");
                    const proformas = g
                      .map((r) => linesInProforma[r.id])
                      .filter((p): p is NonNullable<typeof p> => p != null);
                    const ci = first.customerInvoiceRef;
                    const recebidoEm = dataCurta(ci?.receivedAt);
                    return (
                      <Fragment key={key}>
                        <tr
                          className={`cursor-pointer hover:bg-neutral-800/30 ${allLocked && !tudoPago ? "opacity-70" : ""}`}
                          onClick={() => toggleExpanded(key)}
                          data-testid="pagtec-car-row"
                        >
                          <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={groupSelected}
                              onChange={() =>
                                setSelected((prev) => {
                                  const next = new Set(prev);
                                  if (groupSelected) freeIds.forEach((id) => next.delete(id));
                                  else freeIds.forEach((id) => next.add(id));
                                  return next;
                                })
                              }
                              disabled={allLocked}
                              title={allLocked ? t("technicianPayments.lineInProforma") : undefined}
                            />
                          </td>
                          {/* F-pagtec-vehicle — logo + placa (fallback VIN) + modelo */}
                          <td className="px-3 py-2 text-xs">
                            <div className="flex items-center gap-2">
                              <CarLogo make={first.dossier.vehicleMakeSnapshot} size={20} />
                              <div className="min-w-0">
                                <div className="font-mono font-semibold text-neutral-100">
                                  {first.dossier.plate ?? first.dossier.vin ?? "—"}
                                </div>
                                <div className="truncate text-neutral-500">
                                  {[
                                    first.dossier.vehicleMakeSnapshot,
                                    first.dossier.vehicleModelSnapshot,
                                  ]
                                    .filter(Boolean)
                                    .join(" ") || first.dossier.dossierCode}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-2 max-w-[220px]">
                            <div className="truncate">{first.customer.displayName}</div>
                            {first.dossier.endCustomerName && (
                              <div className="truncate text-[11px] text-neutral-500">
                                ↳ {first.dossier.endCustomerName}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap">
                            <div className="font-mono">
                              {ci?.invoiceNumber ?? first.dossier.invoiceNumberExternal ?? "—"}
                            </div>
                            {clientePagou ? (
                              <div className="text-lime">
                                {recebidoEm
                                  ? `${t("technicianPayments.receivedOn")} ${recebidoEm}`
                                  : t("technicianPayments.receivedWord")}
                              </div>
                            ) : (
                              <div className="text-neutral-500">
                                {t("technicianPayments.notReceived")}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums whitespace-nowrap">
                            {semComissao ? (
                              <span className="badge b-dang">
                                {t("technicianPayments.noCommissionTag")}
                              </span>
                            ) : (
                              formatMoney(toPay, { decimals: 2 })
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <span className="flex flex-wrap gap-1">
                              {tudoPago ? (
                                <span className="badge b-ok">
                                  {t("technicianPayments.optPaid")}
                                </span>
                              ) : cancelado ? (
                                <span className="badge b-mut">
                                  {t("technicianPayments.optCancelled")}
                                </span>
                              ) : clientePagou ? (
                                <span className="badge b-warn">
                                  {t("technicianPayments.pillReleased")}
                                </span>
                              ) : (
                                <span className="badge b-mut">
                                  {t("technicianPayments.pillWaiting")}
                                </span>
                              )}
                              {proformas.length > 0 && (
                                <span className="badge b-info">🔒 {proformas[0]!.proformaRef}</span>
                              )}
                            </span>
                          </td>
                          <td className="px-2 py-2 text-neutral-500 text-xs">
                            {aberto ? "▾" : "▸"}
                          </td>
                        </tr>
                        {aberto && (
                          <tr className="bg-neutral-950/40">
                            <td />
                            <td colSpan={6} className="px-3 py-3">
                              <div className="grid gap-4 md:grid-cols-3 text-xs">
                                <div className="flex flex-col gap-1.5">
                                  <span className="text-[10px] uppercase tracking-wider text-neutral-500">
                                    {t("technicianPayments.colDossier")}
                                  </span>
                                  <Link
                                    href={`/dossiers/${first.dossierId}`}
                                    className="font-mono text-lime hover:underline w-fit"
                                  >
                                    {first.dossier.dossierCode}
                                  </Link>
                                  {/* F-pagtec-devis (CEO 2026-09-27) — valor do Devis
                                      (e o faturado) junto do dossier, ao abrir o carro. */}
                                  <span className="flex flex-wrap gap-x-3 text-[11px]">
                                    <span>
                                      <span className="text-neutral-500">
                                        {t("technicianPayments.devisLabel")}
                                      </span>{" "}
                                      <span className="font-mono font-semibold">
                                        {formatMoney(
                                          sum((r) => r.orcamento),
                                          { decimals: 2 },
                                        )}
                                      </span>
                                    </span>
                                    <span>
                                      <span className="text-neutral-500">
                                        {t("technicianPayments.invoicedLabel")}
                                      </span>{" "}
                                      <span className="font-mono">
                                        {formatMoney(
                                          sum((r) => r.faturado),
                                          { decimals: 2 },
                                        )}
                                      </span>
                                    </span>
                                  </span>
                                  <span className="flex flex-wrap gap-1">
                                    {g.map((r) => (
                                      <span
                                        key={r.id}
                                        className={`w-fit rounded px-1.5 py-0.5 text-[10px] ${
                                          r.serviceType === "dsp"
                                            ? "bg-sky-500/15 text-sky-300"
                                            : "bg-neutral-700/40 text-neutral-300"
                                        }`}
                                      >
                                        {serviceLabel(r.serviceType)} ·{" "}
                                        {formatMoney(
                                          Number(
                                            r.technicianAmount ?? r.technicianCommissionAmount ?? 0,
                                          ),
                                          { decimals: 2 },
                                        )}
                                      </span>
                                    ))}
                                  </span>
                                  {proformas.map((p) => (
                                    <Link
                                      key={p.proformaId}
                                      href={`/proformas/${p.proformaId}`}
                                      className="text-amber-300 hover:underline w-fit"
                                    >
                                      🔒 {t("technicianPayments.inProforma")} {p.proformaRef} ·{" "}
                                      {p.status}
                                    </Link>
                                  ))}
                                </div>
                                <div className="flex flex-col gap-1.5">
                                  <span className="text-[10px] uppercase tracking-wider text-neutral-500">
                                    {t("dossiers.colRepartition")}
                                  </span>
                                  <CommissionCascade
                                    facturado={sum((r) => r.faturado)}
                                    clientPct={pctIfUniform((r) => r.clientPct)}
                                    techPct={pctIfUniform((r) => r.technicianPct)}
                                    intermPct={pctIfUniform((r) => r.intermediaryPct)}
                                    eurostormPct={pctIfUniform((r) => r.eurostormPct)}
                                    clientAmount={sum((r) => r.clientAmount)}
                                    techAmount={
                                      g.some((r) => r.technicianAmount != null)
                                        ? sum((r) => r.technicianAmount)
                                        : null
                                    }
                                    intermAmount={sum((r) => r.intermediaryAmount)}
                                    eurostormAmount={sum((r) => r.eurostormAmount)}
                                    commissionModel={first.commissionModel}
                                    commissionBase={first.commissionBase}
                                    legacyTechAmount={
                                      g.some((r) => r.technicianCommissionAmount != null)
                                        ? sum((r) => r.technicianCommissionAmount)
                                        : null
                                    }
                                    companyName={
                                      accessibleCompanies.find((c) => c.id === companyId)?.name ??
                                      "—"
                                    }
                                  />
                                </div>
                                <div className="flex flex-col gap-1.5">
                                  <span className="text-[10px] uppercase tracking-wider text-neutral-500">
                                    {t("technicianPayments.techInvoiceLabel")}
                                  </span>
                                  {invoices.size === 0 ? (
                                    <span className="text-neutral-500">
                                      {t("technicianPayments.noTechInvoice")}
                                    </span>
                                  ) : (
                                    <>
                                      {[...invoices.values()].map((ti) => (
                                        <span key={ti.id} className="font-mono">
                                          {ti.invoiceNumberExternal} ·{" "}
                                          {formatMoney(Number(ti.totalAmount), { decimals: 2 })}
                                        </span>
                                      ))}
                                      <span className="flex flex-wrap gap-1">
                                        {invStatuses.map((st) => (
                                          <span
                                            key={st}
                                            className={`inline-block rounded px-2 py-0.5 text-[11px] ${INVOICE_STATUS_TONE[st]}`}
                                          >
                                            {INVOICE_STATUS_LABEL[st]}
                                          </span>
                                        ))}
                                        {attach > 0 && (
                                          <span className="text-lime">📎 {attach}</span>
                                        )}
                                      </span>
                                    </>
                                  )}
                                  <span>
                                    {t("technicianPayments.colPaid")}:{" "}
                                    <span className="font-mono">
                                      {formatMoney(paid, { decimals: 2 })}
                                    </span>
                                  </span>
                                  {canEdit && !tudoPago && (
                                    <div className="flex flex-wrap gap-1 pt-1">
                                      {g.map((r) => (
                                        <button
                                          key={r.id}
                                          onClick={() => openPayDialog(r)}
                                          disabled={!r.technicianInvoice}
                                          className="rounded-md border border-neutral-800 hover:border-lime hover:text-lime disabled:opacity-30 px-2 py-1 text-[11px]"
                                          title={
                                            r.technicianInvoice
                                              ? t("technicianPayments.payTitle")
                                              : t("technicianPayments.payTitleNoInvoice")
                                          }
                                        >
                                          {t("technicianPayments.payBtn")}
                                          {g.length > 1 ? ` · ${serviceLabel(r.serviceType)}` : ""}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {total > PAGE_SIZE && (
            <Paginacao
              total={total}
              pageSize={PAGE_SIZE}
              offset={offset}
              onOffset={setOffset}
              noun={
                total === 1
                  ? t("technicianPayments.entrySingular")
                  : t("technicianPayments.entryPlural")
              }
              testId="pagamentosTecnicos-pagination"
            />
          )}
        </>
      )}

      {/* Selection bar — F-roles: escondida p/ visualizador (Gerar proforma é escrita) */}
      {canEdit && selected.size > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 w-[min(95vw,720px)]">
          <div className="flex items-center gap-4 bg-neutral-900 border border-lime/40 shadow-2xl rounded-2xl px-5 py-3">
            <div className="flex flex-col">
              <span className="text-[10px] uppercase tracking-wider text-neutral-500">
                {t("technicianPayments.selectionLabel")}
              </span>
              <span className="text-sm font-semibold">
                {selected.size}{" "}
                {selected.size === 1
                  ? t("technicianPayments.lineSingular")
                  : t("technicianPayments.linePlural")}
              </span>
            </div>
            <div className="h-9 w-px bg-neutral-800" />
            <div className="flex flex-col">
              <span className="text-[10px] uppercase tracking-wider text-neutral-500">
                {t("technicianPayments.totalToPay")}
              </span>
              <span className="text-base font-bold tabular-nums text-lime font-mono">
                {formatMoney(selectedHasInterm ? selectedSum + selectedInterm : selectedSum, {
                  decimals: 2,
                })}
              </span>
              {selectedAdiant > 0 && (
                <span className="text-[10px] text-neutral-400" data-testid="sel-adiant">
                  {t("technicianPayments.selReleasedPart")}{" "}
                  {formatMoney(selectedLiberado, { decimals: 2 })} +{" "}
                  <span className="font-semibold text-amber-300">
                    {t("technicianPayments.selAdvancePart")}{" "}
                    {formatMoney(selectedAdiant, { decimals: 2 })}
                  </span>
                </span>
              )}
              {selectedAdiant > 0 && disponivel != null && selectedAdiant > disponivel + 0.01 && (
                <span className="text-[10px] text-red-400">
                  ⚠ {t("technicianPayments.selAdvanceOver")}{" "}
                  {formatMoney(disponivel, { decimals: 2 })}
                </span>
              )}
              {selectedHasInterm && (
                <span className="text-[10px] text-neutral-400">
                  {t("technicianPayments.selTechPart")} {formatMoney(selectedSum, { decimals: 2 })}{" "}
                  + {t("technicianPayments.selIntermPart")}{" "}
                  {formatMoney(selectedInterm, { decimals: 2 })}
                  {intermName ? ` → ${intermName}` : ""}
                </span>
              )}
            </div>
            {!selectedAllSameTechnician && (
              <span className="text-xs text-red-400 ml-2">
                ⚠ {t("technicianPayments.diffTechnicians")}
              </span>
            )}
            <div className="flex-1" />
            <button
              onClick={() => setProformaDialog(true)}
              disabled={!selectedAllSameTechnician || selected.size === 0}
              className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-30 px-4 py-1.5 text-sm font-medium"
            >
              {t("technicianPayments.generateProforma")}
            </button>
            {isAdmin && (
              <button
                onClick={() => setPorForaDialog(true)}
                disabled={!selectedAllSameTechnician || selected.size === 0}
                className="rounded-md border border-lime/50 text-lime hover:bg-lime/10 disabled:opacity-30 px-3 py-1.5 text-sm"
                data-testid="external-payment-btn"
                title={t("technicianPayments.extBtnHint")}
              >
                {t("technicianPayments.extBtn")}
              </button>
            )}
            <button
              onClick={clearSelection}
              className="rounded-md border border-neutral-800 hover:border-neutral-700 px-3 py-1.5 text-xs"
            >
              {t("technicianPayments.clearBtn")}
            </button>
          </div>
        </div>
      )}

      {canEdit && payDialog && companyId !== null && (
        <PayDialog
          state={payDialog}
          companyId={companyId}
          onClose={() => setPayDialog(null)}
          onSuccess={() => {
            setPayDialog(null);
            utils.serviceLines.listPagamentosTecnicos.invalidate();
            utils.serviceLines.caixaRepasse.invalidate();
          }}
        />
      )}

      {isAdmin && porForaDialog && companyId !== null && selectedTechnicianId !== null && (
        <ExternalPaymentDialog
          companyId={companyId}
          technicianId={selectedTechnicianId}
          technicianName={selectedTechnicianName}
          serviceLineIds={Array.from(selected)}
          total={selectedSum}
          intermediario={selectedInterm}
          onClose={() => setPorForaDialog(false)}
          onDone={(id) => {
            setPorForaDialog(false);
            clearSelection();
            utils.serviceLines.listPagamentosTecnicos.invalidate();
            utils.serviceLines.caixaRepasse.invalidate();
            window.location.href = `/proformas/${id}`;
          }}
        />
      )}

      {canEdit && proformaDialog && companyId !== null && selectedTechnicianId !== null && (
        <ProformaDialog
          companyId={companyId}
          technicianId={selectedTechnicianId}
          serviceLineIds={Array.from(selected)}
          onClose={() => setProformaDialog(false)}
          onCreated={(id) => {
            setProformaDialog(false);
            clearSelection();
            utils.serviceLines.listPagamentosTecnicos.invalidate();
            utils.serviceLines.caixaRepasse.invalidate();
            window.location.href = `/proformas/${id}`;
          }}
        />
      )}
    </Shell>
  );
}

// ─── Pay dialog ────────────────────────────────────────────────────────────────

interface PayDialogState {
  lineId: number;
  technicianInvoiceId: number;
  technicianName: string;
  invoiceNumber: string;
  defaultAmount: number;
}

const PAYMENT_METHODS = ["bank_transfer", "sepa", "check", "cash", "card", "other"] as const;
type PaymentMethod = (typeof PAYMENT_METHODS)[number];

function PayDialog({
  state,
  companyId,
  onClose,
  onSuccess,
}: {
  state: PayDialogState;
  companyId: number;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const t = useT();
  const [amount, setAmount] = useState(state.defaultAmount.toFixed(2));
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("bank_transfer");
  const [paidAt, setPaidAt] = useState(new Date().toISOString().slice(0, 16));
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [gateError, setGateError] = useState<string | null>(null);
  const [otherError, setOtherError] = useState<string | null>(null);
  const [showOverride, setShowOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");

  const createMutation = trpc.technicianPayments.create.useMutation({
    onSuccess: () => onSuccess(),
    onError: (e) => {
      const msg = e.message;
      if (msg.includes("Payment gate violado")) {
        setGateError(msg);
        setOtherError(null);
        setShowOverride(true);
      } else {
        setOtherError(msg);
        setGateError(null);
      }
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setGateError(null);
    setOtherError(null);
    const amountNum = parseFloat(amount.replace(",", "."));
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      setOtherError(t("technicianPayments.invalidAmount"));
      return;
    }
    createMutation.mutate({
      companyId,
      technicianInvoiceId: state.technicianInvoiceId,
      amount: amountNum,
      currency: "EUR",
      paidAt: new Date(paidAt).toISOString(),
      paymentMethod,
      reference: reference.trim() || undefined,
      notes: notes.trim() || undefined,
      paymentGateOverride:
        showOverride && overrideReason.trim().length >= 5
          ? { reason: overrideReason.trim() }
          : undefined,
    });
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
      <form
        onSubmit={handleSubmit}
        className="bg-neutral-950 border border-neutral-800 rounded-lg p-6 w-full max-w-md space-y-4"
      >
        <h2 className="text-lg font-semibold">{t("technicianPayments.payDialogTitle")}</h2>
        <div className="text-xs text-neutral-400">
          {t("technicianPayments.technicianLabel")}:{" "}
          <span className="text-neutral-200">{state.technicianName}</span> ·{" "}
          {t("technicianPayments.invoiceLabel")}{" "}
          <span className="font-mono text-neutral-200">{state.invoiceNumber}</span>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1">
              {t("technicianPayments.amountLabel")}
            </label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
              className={inputClass + " w-full font-mono"}
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1">
              {t("technicianPayments.methodLabel")}
            </label>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
              className={inputClass + " w-full"}
            >
              <option value="bank_transfer">{t("technicianPayments.methodBankTransfer")}</option>
              <option value="sepa">SEPA</option>
              <option value="check">{t("technicianPayments.methodCheck")}</option>
              <option value="cash">{t("technicianPayments.methodCash")}</option>
              <option value="card">{t("technicianPayments.methodCard")}</option>
              <option value="other">{t("technicianPayments.methodOther")}</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1">
            {t("technicianPayments.paidAtLabel")}
          </label>
          <input
            type="datetime-local"
            value={paidAt}
            onChange={(e) => setPaidAt(e.target.value)}
            required
            className={inputClass + " w-full"}
          />
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1">
            {t("technicianPayments.referenceLabel")}
          </label>
          <input
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            maxLength={200}
            className={inputClass + " w-full"}
          />
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1">
            {t("technicianPayments.notesLabel")}
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={2000}
            rows={2}
            className={inputClass + " w-full resize-y"}
          />
        </div>

        {gateError && (
          <div className="text-amber-300 text-xs bg-amber-500/10 border border-amber-500/30 rounded-md px-3 py-2 space-y-2">
            <div className="font-medium">{t("technicianPayments.gateBlocked")}</div>
            <div className="whitespace-pre-wrap break-words text-amber-200/80">{gateError}</div>
            <div className="pt-2 border-t border-amber-500/20">
              <label className="flex items-center gap-2 text-amber-200">
                <input
                  type="checkbox"
                  checked={showOverride}
                  onChange={(e) => setShowOverride(e.target.checked)}
                />
                {t("technicianPayments.forceOverride")}
              </label>
              {showOverride && (
                <input
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  placeholder={t("technicianPayments.overridePlaceholder")}
                  minLength={5}
                  required={showOverride}
                  className={inputClass + " w-full mt-2"}
                />
              )}
            </div>
          </div>
        )}

        {otherError && (
          <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
            {otherError}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={createMutation.isPending}
            className="rounded-md border border-neutral-800 hover:border-neutral-700 px-4 py-1.5 text-sm"
          >
            {t("technicianPayments.cancel")}
          </button>
          <button
            type="submit"
            disabled={
              createMutation.isPending || (showOverride && overrideReason.trim().length < 5)
            }
            className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-40 px-4 py-1.5 text-sm font-medium"
          >
            {createMutation.isPending
              ? t("technicianPayments.saving")
              : t("technicianPayments.register")}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── Proforma dialog ───────────────────────────────────────────────────────────

interface CompanyLite {
  id: number;
  name: string;
  isGroupMember: boolean;
}

function ProformaDialog({
  companyId,
  technicianId,
  serviceLineIds,
  onClose,
  onCreated,
}: {
  companyId: number;
  technicianId: number;
  serviceLineIds: number[];
  onClose: () => void;
  onCreated: (id: number) => void;
}) {
  const t = useT();
  const [retentionPercent, setRetentionPercent] = useState("0");
  const [commissionPercent, setCommissionPercent] = useState("");
  const [language, setLanguage] = useState<"fr" | "en" | "pt">("fr");
  const [customMessage, setCustomMessage] = useState("");
  const [error, setError] = useState<string | null>(null);

  // CEO 2026-10-18 — quem recebe é decidido no SERVIDOR pelo par técnico ×
  // cliente de cada carro (proforma-flow.ts), não pelo cadastro do técnico.
  // F-peca-paga-uma-vez — as metades de dégarnissage (id negativo) entram
  // na proforma do técnico que as fez.
  const previewQuery = trpc.proformaRequests.previewTechnicianFlow.useQuery({
    companyId,
    technicianId,
    serviceLineIds,
  });
  const flowPreview = previewQuery.data ?? null;
  const bloqueado =
    previewQuery.isLoading ||
    previewQuery.isError ||
    flowPreview == null ||
    flowPreview.conflict != null ||
    flowPreview.external != null;
  const pctTxt = (n: number) => `${n.toFixed(2).replace(/\.?0+$/, "")} %`;

  const createMutation = trpc.proformaRequests.createForTechnician.useMutation({
    onSuccess: (data) => onCreated(data.id),
    onError: (e) => setError(e.message),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const ret = parseFloat(retentionPercent.replace(",", "."));
    if (!Number.isFinite(ret) || ret < 0 || ret > 100) {
      setError(t("technicianPayments.retentionInvalid"));
      return;
    }
    let comm: number | undefined;
    if (commissionPercent.trim() !== "") {
      const c = parseFloat(commissionPercent.replace(",", "."));
      if (!Number.isFinite(c) || c < 0 || c > 100) {
        setError(t("technicianPayments.commissionInvalid"));
        return;
      }
      comm = c;
    }
    createMutation.mutate({
      companyId,
      technicianId,
      serviceLineIds,
      retentionPercent: ret,
      commissionPercent: comm,
      language,
      customMessage: customMessage.trim() || undefined,
    });
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
      <form
        onSubmit={handleSubmit}
        className="bg-neutral-950 border border-neutral-800 rounded-lg p-6 w-full max-w-lg space-y-4"
      >
        <h2 className="text-lg font-semibold">{t("technicianPayments.proformaDialogTitle")}</h2>
        <div className="text-xs text-neutral-400">
          {serviceLineIds.length}{" "}
          {serviceLineIds.length === 1
            ? t("technicianPayments.lineSelectedSingular")
            : t("technicianPayments.lineSelectedPlural")}
          .
        </div>

        {/* Preview do fluxo detectado */}
        <div className="rounded-md border border-neutral-800 bg-neutral-900/60 p-3 text-xs space-y-1">
          {previewQuery.isLoading ? (
            <p className="text-neutral-500">{t("technicianPayments.loadingTechnician")}</p>
          ) : previewQuery.isError ? (
            <p className="text-red-300">
              {t("technicianPayments.flowPreviewError")} {previewQuery.error.message}
            </p>
          ) : flowPreview ? (
            <>
              <div className="flex items-center gap-2">
                <span className="text-neutral-500">{t("technicianPayments.flowDetected")}</span>
                <span
                  className={`rounded px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${
                    flowPreview.flow === "direct"
                      ? "bg-lime/15 text-lime"
                      : flowPreview.flow === "group"
                        ? "bg-blue-500/15 text-blue-300"
                        : "bg-violet-500/15 text-violet-300"
                  }`}
                  data-testid="proforma-flow"
                  data-flow={flowPreview.flow}
                >
                  {flowPreview.flow === "direct"
                    ? t("technicianPayments.flowDirect")
                    : flowPreview.flow === "group"
                      ? t("technicianPayments.flowGroup")
                      : t("technicianPayments.flowExternal")}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-neutral-500">{t("technicianPayments.recipientLabel")}</span>
                <span className="text-neutral-200" data-testid="proforma-recipient">
                  {flowPreview.recipientName}
                </span>
              </div>
              <p className="text-[10px] text-neutral-500">
                {t("technicianPayments.flowSourceNote")}
              </p>
              {flowPreview.flow !== "direct" && (
                <p className="text-[10px] text-neutral-500 pt-1">
                  {t("technicianPayments.proformaTechnicianNote")}
                </p>
              )}
              {flowPreview.external && (
                <p
                  className="rounded-md border border-red-500/40 bg-red-500/10 px-2 py-1.5 text-red-200"
                  data-testid="proforma-flow-external"
                >
                  {t("technicianPayments.flowExternalPre")} <b>{flowPreview.external.name}</b>{" "}
                  {t("technicianPayments.flowExternalSuf")}
                </p>
              )}
              {flowPreview.conflict && (
                <p
                  className="rounded-md border border-red-500/40 bg-red-500/10 px-2 py-1.5 text-red-200"
                  data-testid="proforma-flow-conflict"
                >
                  {flowPreview.conflict}
                </p>
              )}
              {flowPreview.degarnissagePending.length > 0 && (
                <div
                  className="space-y-1 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-amber-200"
                  data-testid="proforma-degarnissage-pending"
                >
                  <p className="font-semibold">{t("technicianPayments.degPendingTitle")}</p>
                  <ul>
                    {flowPreview.degarnissagePending.map((d) => (
                      <li key={d.dossierId}>
                        <span className="whitespace-nowrap font-mono">
                          {d.plate ?? d.vin ?? d.dossierCode}
                        </span>{" "}
                        —{" "}
                        {d.own
                          ? t("technicianPayments.degPendingOwn")
                          : t("technicianPayments.degPendingOther")}
                      </li>
                    ))}
                  </ul>
                  <p className="text-amber-200/80">{t("technicianPayments.degPendingHint")}</p>
                </div>
              )}
              {flowPreview.mismatches.length > 0 && (
                <div
                  className="space-y-1 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-amber-200"
                  data-testid="proforma-flow-mismatch"
                >
                  <p className="font-semibold">{t("technicianPayments.flowMismatchTitle")}</p>
                  <ul>
                    {flowPreview.mismatches.map((m, i) => (
                      <li key={i}>
                        <span className="whitespace-nowrap font-mono">{m.plate ?? "—"}</span>:{" "}
                        {t("technicianPayments.flowMismatchPair")} <b>{pctTxt(m.pairPct)}</b> ·{" "}
                        {t("technicianPayments.flowMismatchLines")} <b>{pctTxt(m.linePct)}</b>
                      </li>
                    ))}
                  </ul>
                  <p className="text-amber-200/80">{t("technicianPayments.flowMismatchHint")}</p>
                </div>
              )}
            </>
          ) : (
            <p className="text-neutral-500">{t("technicianPayments.noTechnicianData")}</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1">
              {t("technicianPayments.retentionLabel")}
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={retentionPercent}
              onChange={(e) => setRetentionPercent(e.target.value)}
              required
              className={inputClass + " w-full font-mono"}
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1">
              {t("technicianPayments.commissionLabel")}
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={commissionPercent}
              onChange={(e) => setCommissionPercent(e.target.value)}
              className={inputClass + " w-full font-mono"}
            />
          </div>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1">
            {t("technicianPayments.pdfLanguageLabel")}
          </label>
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value as "fr" | "en" | "pt")}
            className={inputClass + " w-full"}
          >
            <option value="fr">Français</option>
            <option value="en">English</option>
            <option value="pt">Português</option>
          </select>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1">
            {t("technicianPayments.customMessageLabel")}
          </label>
          <textarea
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            maxLength={2000}
            rows={2}
            className={inputClass + " w-full resize-y"}
          />
        </div>

        {error && (
          <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2 whitespace-pre-wrap break-words">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={createMutation.isPending}
            className="rounded-md border border-neutral-800 hover:border-neutral-700 px-4 py-1.5 text-sm"
          >
            {t("technicianPayments.cancel")}
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending || bloqueado}
            className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-40 px-4 py-1.5 text-sm font-medium"
            data-testid="proforma-create-btn"
          >
            {createMutation.isPending
              ? t("technicianPayments.creating")
              : t("technicianPayments.createProforma")}
          </button>
        </div>
      </form>
    </div>
  );
}

const inputClass =
  "rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm focus:border-lime outline-none disabled:opacity-50";

const EXTERNAL_METHODS = ["salario", "dinheiro", "transferencia", "outro"] as const;

/**
 * CEO 2026-10-26 (BR STORM, equipe interna) — o técnico já recebeu FORA da
 * proforma. O Gestion registra (série INT, já paga): as peças passam a pagas,
 * sem e-mail, PDF, fatura do técnico, banco nem adiantamentos.
 */
function ExternalPaymentDialog({
  companyId,
  technicianId,
  technicianName,
  serviceLineIds,
  total,
  intermediario,
  onClose,
  onDone,
}: {
  companyId: number;
  technicianId: number;
  technicianName: string;
  serviceLineIds: number[];
  total: number;
  intermediario: number;
  onClose: () => void;
  onDone: (id: number) => void;
}) {
  const t = useT();
  const hoje = new Date().toISOString().slice(0, 10);
  const [paidAt, setPaidAt] = useState(hoje);
  const [method, setMethod] = useState<(typeof EXTERNAL_METHODS)[number]>("salario");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const mut = trpc.proformaRequests.registerExternalPayment.useMutation({
    onSuccess: (r) => onDone(r.id),
    onError: (e) => setError(e.message),
  });
  const rotulo: Record<(typeof EXTERNAL_METHODS)[number], string> = {
    salario: t("technicianPayments.extMethodSalario"),
    dinheiro: t("technicianPayments.extMethodDinheiro"),
    transferencia: t("technicianPayments.extMethodTransferencia"),
    outro: t("technicianPayments.extMethodOutro"),
  };
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          mut.mutate({ companyId, technicianId, serviceLineIds, paidAt, method, note });
        }}
        className="w-full max-w-lg rounded-lg border border-neutral-800 bg-neutral-950 p-5 space-y-4"
        data-testid="external-payment-dialog"
      >
        <h2 className="text-lg font-semibold">{t("technicianPayments.extTitle")}</h2>
        <p className="text-sm text-neutral-300">
          <b>{technicianName}</b> · {serviceLineIds.length}{" "}
          {serviceLineIds.length === 1
            ? t("technicianPayments.lineSingular")
            : t("technicianPayments.linePlural")}{" "}
          · <span className="font-mono">{formatMoney(total, { decimals: 2 })}</span>
        </p>
        <p className="rounded-md border border-neutral-800 bg-neutral-900 px-3 py-2 text-xs text-neutral-400">
          {t("technicianPayments.extHint")}
        </p>
        {intermediario > 0.005 && (
          <p
            className="badge b-warn whitespace-normal text-xs"
            data-testid="external-payment-interm"
          >
            ⚠ {t("technicianPayments.extIntermPre")} {formatMoney(intermediario, { decimals: 2 })}{" "}
            {t("technicianPayments.extIntermSuf")}
          </p>
        )}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block text-xs uppercase tracking-wider text-neutral-500">
            {t("technicianPayments.extDate")}
            <input
              type="date"
              value={paidAt}
              max={hoje}
              min="2026-01-01"
              onChange={(e) => setPaidAt(e.target.value)}
              className={inputClass + " mt-1 w-full"}
              required
            />
          </label>
          <label className="block text-xs uppercase tracking-wider text-neutral-500">
            {t("technicianPayments.extMethod")}
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value as (typeof EXTERNAL_METHODS)[number])}
              className={inputClass + " mt-1 w-full"}
            >
              {EXTERNAL_METHODS.map((m) => (
                <option key={m} value={m}>
                  {rotulo[m]}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="block text-xs uppercase tracking-wider text-neutral-500">
          {t("technicianPayments.extNote")}
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={1000}
            rows={2}
            placeholder={t("technicianPayments.extNotePh")}
            className={inputClass + " mt-1 w-full resize-y normal-case tracking-normal"}
            data-testid="external-payment-note"
          />
        </label>
        {error && (
          <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2 whitespace-pre-wrap break-words">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            disabled={mut.isPending}
            className="rounded-md border border-neutral-800 hover:border-neutral-700 px-4 py-1.5 text-sm"
          >
            {t("technicianPayments.cancel")}
          </button>
          <button
            type="submit"
            disabled={mut.isPending || note.trim().length < 3 || !paidAt}
            className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-40 px-4 py-1.5 text-sm font-medium"
            data-testid="external-payment-submit"
          >
            {mut.isPending ? t("technicianPayments.creating") : t("technicianPayments.extSubmit")}
          </button>
        </div>
      </form>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <ErpShell>
      <div className="space-y-4">{children}</div>
    </ErpShell>
  );
}
